import type {
  TableV2DataObject,
  TableV2RecordType,
} from '@frontend/legacy-components';

import { TABLE_V2_SELECT_ALL_KEY } from './constants';

/**
 * A function that checks if a given row can be selected by ensuring it is not in the disabledOrOmittedRows.
 * @param record the `record` of the row to check if can be selected.
 * @param disabledOrOmittedRows an array of string keys for rows that should be either disabled or otherwise omitted from selection.
 * @returns
 */
const selectingRowAllowed = (
  record: TableV2RecordType,
  disabledOrOmittedRows?: string[],
) => {
  return !disabledOrOmittedRows?.includes(record.key);
};

/**
 * Retrieve a flat string list of all row keys found recursively within the provided object.
 * Uses depth-first search.
 * @param record An TableV2Record object.
 * @param disabledOrOmittedRows an array of string keys for rows that should be either disabled or otherwise omitted from selection.
 * @param includeSelf flag to include the key of the record you pass in to the child keys
 * @returns A string list of all row keys found recursively within the provided `record`.
 */
export const getNestedChildKeysFromRecordDepthFirst = (
  record: TableV2RecordType,
  disabledOrOmittedRows: string[] = [],
  includeSelf = true,
): string[] => {
  // Create a results store;
  const result: string[] = [];
  if (includeSelf && selectingRowAllowed(record, disabledOrOmittedRows)) {
    result.push(record.key);
  }

  // If we have children,
  if (record.children != null) {
    // Iterate through children recursively and add their keys to store.
    record.children.forEach((child) => {
      // If the child has children,
      if (child.children != null) {
        // Spread the recursive run of this function
        // (which includes this child's key) to the results store.
        result.push(
          ...getNestedChildKeysFromRecordDepthFirst(
            child,
            disabledOrOmittedRows,
            true,
          ),
        );
      }
      // If this child doesn't have children,
      else {
        // Push the child's key to the result store.
        if (selectingRowAllowed(child, disabledOrOmittedRows)) {
          result.push(child.key);
        }
      }
    });
  }
  return result;
};

/**
 * Carry out the effects of clicking an enabled checkbox of a row with children (a "parent" checkbox).
 * @param dataObj the entire `dataObj` for all rows
 * @param key the key of the row whose checkbox is being clicked.
 * @param isIndeterminate whether this row is currently
 * @param selectedRows the state object storing selected row keys.
 * @param updateSelectedRows function that modifies both the `selectedRows` and `selectedRowsUnique` state objects
 * @param disabledOrOmittedRows an array of string keys for rows that should be either disabled or otherwise omitted from selection.
 */
export const selectChildren = (
  dataObj: TableV2DataObject,
  key: string,
  isIndeterminate: boolean,
  selectedRows: string[],
  updateSelectedRows: (nextSelectedRows: string[]) => void,
  disabledOrOmittedRows: string[],
) => {
  // If the provided key is valid...
  if (key in dataObj) {
    // Retrieve the keys of all children.
    // If this is the main parent for the table...
    // Skip retrieving the record object from the key and use the record as children instead.
    const keysToApply = getNestedChildKeysFromRecordDepthFirst(
      dataObj[key] as TableV2RecordType,
      disabledOrOmittedRows,
    );

    let nextSelectedRows = null;

    // If this key is in selected and we're not indeterminate (meaning this and all of its children are in selected.)
    if (selectedRows.includes(key) && !isIndeterminate) {
      // Create an array based on selected where this key and all of its children's keys are removed.
      nextSelectedRows = selectedRows.filter((el) => !keysToApply.includes(el));
    }
    // Otherwise, if the key isn't in selected; or if it is, but it is also indeterminate,
    else {
      // filter keysToApply into only the unique keys not already found in selected...
      // ...so we don't create any duplicates as we add them to selected.
      nextSelectedRows = [
        ...selectedRows,
        ...keysToApply.filter((el) => !selectedRows.includes(el)),
      ];
    }

    if (nextSelectedRows !== null) {
      updateSelectedRows(nextSelectedRows);
    }
  }
};

/**
 * Carry out the effects of holding shift and clicking a checkbox after previous clicking a different checkbox.
 * @param dataObj the entire `dataObj` for all rows
 * @param selectedRows the state object storing selected row keys.
 * @param updateSelectedRows function that modifies both the `selectedRows` and `selectedRowsUnique` state objects
 * @param disabledOrOmittedRows an array of string keys for rows that should be either disabled or otherwise omitted from selection.
 * @param lastSelected the key of the most-recently-selected row.
 * @param record the `record` of the row of the checkbox being shift-clicked.
 * @param isIndeterminate if the checkbox is currently in an indeterminate state.
 */
export const selectRange = (
  dataObj: TableV2DataObject,
  selectedRows: string[],
  updateSelectedRows: (nextSelectedRows: string[]) => void,
  disabledOrOmittedRows: string[],
  lastSelected: string,
  record: TableV2RecordType,
  isIndeterminate: boolean,
) => {
  const parentDataObject = dataObj[TABLE_V2_SELECT_ALL_KEY];

  // Get all keys on the current page (in top-down order) so we can select a range from within them.
  const allKeys = getNestedChildKeysFromRecordDepthFirst(
    parentDataObject,
    disabledOrOmittedRows,
  );

  const lastSelectedIndex = allKeys.indexOf(lastSelected);
  const justSelectedIndex = allKeys.indexOf(record.key);
  const selectingUpward = lastSelectedIndex >= justSelectedIndex;

  const keysInRange = selectingUpward
    ? allKeys.slice(justSelectedIndex, lastSelectedIndex + 1)
    : allKeys.slice(lastSelectedIndex, justSelectedIndex + 1);

  let nextSelectedRows = null;

  // if this key is already selected, de-deselect all of the range.
  // ... and not removing parents, trusting that they'll be removed by their own useEffect child checks.
  if (selectedRows.includes(record.key)) {
    nextSelectedRows = selectedRows.filter(
      (key) => !keysInRange.includes(key) && !dataObj[key]?.children,
    );
  }
  // else if the checkbox just clicked, currently isn't selected,
  else {
    // Select the whole range...
    // ...adding new elements, no duplicates...
    // ... and not adding parents, trusting that they'll be added by their own useEffect child checks.
    nextSelectedRows = [
      ...selectedRows,
      ...keysInRange.filter(
        (key) => !selectedRows.includes(key) && !dataObj[key]?.children,
      ),
    ];
  }

  if (nextSelectedRows !== null) {
    updateSelectedRows(nextSelectedRows);
  }

  if (!selectingUpward && record.children != null) {
    selectChildren(
      dataObj,
      record.key,
      isIndeterminate,
      selectedRows,
      updateSelectedRows,
      disabledOrOmittedRows,
    );
  }
};

/**
 * Determine if a checkbox of a row with children (a "parent" checkbox) should be empty, mixed, or full.
 * @param record the `record` object to check the state of.
 * @param selectedRows the state object storing selected row keys.
 * @param disabledOrOmittedRows an array of string keys for rows that should be either disabled or otherwise omitted from selection.
 * @returns {Object} `{mixed: boolean, empty: boolean, full: boolean}`
 */
export const getParentCheckboxState = (
  record: TableV2RecordType,
  selectedRows: string[],
  disabledOrOmittedRows: string[],
): { mixed: boolean; empty: boolean; full: boolean } => {
  let mixed = false;
  let empty = false;
  let full = false;

  if (record.children != null) {
    // Get the child keys not including our own key.
    const childKeys = getNestedChildKeysFromRecordDepthFirst(
      record,
      disabledOrOmittedRows,
      false,
    );

    // Count the number of occurrences of child keys in selected to make judgments about our state.
    let occurrences = 0;
    childKeys.forEach((childKey) => {
      if (selectedRows?.includes(childKey)) {
        occurrences += 1;
      }
    });

    mixed = occurrences >= 1 && occurrences < childKeys.length;
    empty = occurrences === 0;
    full = occurrences === childKeys.length && !empty;
  }

  return { mixed, empty, full };
};

/**
 * Take in a nested array of data and flatten it out into an object to work
 * nicely with the selection context
 * @param data
 */
export const flattenDataToOrderedObject = (
  data: TableV2RecordType[],
  accumulator: TableV2DataObject = {},
): TableV2DataObject => {
  data.forEach((record) => {
    accumulator[record.key] = record;
    if (record.children) {
      flattenDataToOrderedObject(record.children, accumulator);
    }
  });
  return accumulator;
};
